import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pencil, KeyRound, LogIn, Search, Plus, Trash2, Skull, UserX, ArrowLeftRight, Check, X, Link2, Eye, LockOpen, Clock, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DeclineReasonBox from "@/components/DeclineReasonBox";
import { useAuth } from "@/context/AuthContext";
import { useReference } from "@/lib/useReference";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import ConfirmDialog from "@/components/ConfirmDialog";

const ROLE_LABEL = { team_member: "Team Member", coach: "Coach", administrator: "System Administrator", district_administrator: "District Admin", campus_administrator: "Campus Admin", parent: "Parent" };

function ViewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="font-medium text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800">{value || "—"}</span>
    </div>
  );
}

function StudentPicker({ roster, selected, onToggle, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-student-picker`}>
      {roster.length === 0 ? <span className="text-xs text-slate-400">No approved team members on this parent's team.</span>
        : roster.map((m) => {
          const on = selected.includes(m.id);
          return (
            <button type="button" key={m.id} onClick={() => onToggle(m.id)} data-testid={`${testPrefix}-student-${m.id}`}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {m.last_name}, {m.first_name}
            </button>
          );
        })}
    </div>
  );
}

function EditDialog({ open, setOpen, acct, onDone }) {
  const { ref } = useReference();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", role: "team_member", is_active: true, district_id: "", campus_id: "", team_id: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (acct) setForm({ first_name: acct.first_name || "", last_name: acct.last_name || "", email: acct.email || "", role: acct.role, is_active: acct.is_active !== false, district_id: acct.is_staff ? "__staff__" : (acct.district_id || ""), campus_id: acct.campus_id || "", team_id: acct.team_id || "" });
  }, [acct, open]);
  const isStaff = form.district_id === "__staff__";
  const campuses = ref.campuses.filter((c) => c.district_id === form.district_id);
  const teams = ref.teams.filter((t) => t.campus_id === form.campus_id);
  const submit = async () => {
    setLoading(true);
    try {
      const payload = { first_name: form.first_name, last_name: form.last_name, email: form.email, role: form.role, is_active: form.is_active, is_staff: isStaff };
      if (!isStaff && form.team_id) { payload.district_id = form.district_id; payload.campus_id = form.campus_id; payload.team_id = form.team_id; }
      await api.put(`/admin/accounts/${acct.id}`, payload);
      toast.success("Account updated"); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="account-edit-dialog" className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit account</DialogTitle><DialogDescription>Update this account's details, role, active status, or district/team.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="account-first-input" /></div>
            <div className="space-y-2"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="account-last-input" /></div>
          </div>
          <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="account-email-input" /></div>
          <div className="space-y-2"><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="account-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="team_member">Team Member</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="administrator">System Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>District</Label>
            <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v, campus_id: "", team_id: "" }))}>
              <SelectTrigger data-testid="account-district-select"><SelectValue placeholder="Select district" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__staff__" data-testid="account-district-staff">Staff</SelectItem>
                {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Campus</Label>
              <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v, team_id: "" }))} disabled={!form.district_id || isStaff}>
                <SelectTrigger data-testid="account-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                <SelectContent>
                  {campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Team</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v }))} disabled={!form.campus_id || isStaff}>
                <SelectTrigger data-testid="account-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
                <SelectContent>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.code ? ` (${t.code})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="account-active-switch" /><Label>Active (can log in)</Label></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={loading} data-testid="account-save-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ open, setOpen, acct }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { setPw(""); }, [open]);
  const submit = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try { await api.post(`/admin/accounts/${acct.id}/set-password`, { new_password: pw }); toast.success("Password updated"); setOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="account-password-dialog">
        <DialogHeader><DialogTitle className="font-heading">Set password — {acct?.first_name} {acct?.last_name}</DialogTitle><DialogDescription>Set a new temporary password for this account.</DialogDescription></DialogHeader>
        <div className="space-y-2"><Label>New password</Label><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min 6 characters" data-testid="account-new-password-input" /></div>
        <DialogFooter><Button onClick={submit} disabled={loading} data-testid="account-password-save-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">Set password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, setOpen, onDone }) {
  const { ref } = useReference();
  const empty = { first_name: "", last_name: "", email: "", password: "", role: "team_member", district_id: "", campus_id: "", team_id: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setForm(empty); }, [open]);
  const needsTeam = form.role === "team_member" || form.role === "coach" || form.role === "parent";
  const needsDistrict = form.role === "district_administrator" || form.role === "campus_administrator" || needsTeam;
  const needsCampus = form.role === "campus_administrator" || needsTeam;
  const campuses = ref.campuses.filter((c) => c.district_id === form.district_id);
  const teams = ref.teams.filter((t) => t.campus_id === form.campus_id);
  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email) { toast.error("Name and email are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (needsTeam && !form.team_id) { toast.error("Select district, campus and team"); return; }
    if (form.role === "district_administrator" && !form.district_id) { toast.error("Select a district"); return; }
    if (form.role === "campus_administrator" && !form.campus_id) { toast.error("Select district and campus"); return; }
    setLoading(true);
    try {
      const payload = { first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password, role: form.role };
      if (needsTeam) { payload.district_id = form.district_id; payload.campus_id = form.campus_id; payload.team_id = form.team_id; }
      else if (form.role === "district_administrator") { payload.district_id = form.district_id; }
      else if (form.role === "campus_administrator") { payload.district_id = form.district_id; payload.campus_id = form.campus_id; }
      await api.post("/admin/accounts", payload);
      toast.success("Account created"); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="account-create-dialog" className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Add account</DialogTitle><DialogDescription>Create a new system administrator, coach, or team member. They can log in immediately.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="create-first-input" /></div>
            <div className="space-y-2"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="create-last-input" /></div>
          </div>
          <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="create-email-input" /></div>
          <div className="space-y-2"><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" data-testid="create-password-input" /></div>
          <div className="space-y-2"><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v, district_id: "", campus_id: "", team_id: "" }))}>
              <SelectTrigger data-testid="create-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="team_member">Team Member</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="campus_administrator">Campus Administrator</SelectItem>
                <SelectItem value="district_administrator">District Administrator</SelectItem>
                <SelectItem value="administrator">System Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {needsDistrict && (
            <>
              <div className="space-y-2"><Label>District</Label>
                <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v, campus_id: "", team_id: "" }))}>
                  <SelectTrigger data-testid="create-district-select"><SelectValue placeholder="Select district" /></SelectTrigger>
                  <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {needsCampus && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Campus</Label>
                  <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v, team_id: "" }))} disabled={!form.district_id}>
                    <SelectTrigger data-testid="create-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                    <SelectContent>{campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {needsTeam && (
                <div className="space-y-2"><Label>Team</Label>
                  <Select value={form.team_id} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v }))} disabled={!form.campus_id}>
                    <SelectTrigger data-testid="create-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                )}
              </div>
              )}
            </>
          )}
        </div>
        <DialogFooter><Button onClick={submit} disabled={loading} data-testid="account-create-save-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">Create account</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DEL_ROLE_LABEL = { team_member: "Team Member", parent: "Parent" };
const fmtDReq = (v) => v ? new Date(v).toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

function DeletionRequestsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [statusFilter, setStatusFilter] = useState("needs_action");
  const [page, setPage] = useState(1);
  const PAGE = 6;
  const load = useCallback(() => api.get("/admin/deletion-requests").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter]);

  const act = async (rid, action, okMsg) => {
    setBusy(rid);
    try { await api.post(`/admin/deletion-requests/${rid}/${action}`); toast.success(okMsg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const NEEDS_ACTION = ["awaiting_activation", "window_active", "window_expired", "coach_requested"];
  const filtered = items.filter((r) => statusFilter === "all" ? true
    : statusFilter === "needs_action" ? NEEDS_ACTION.includes(r.status)
    : r.status === statusFilter);
  if (items.length === 0) return null;

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE));
  const shown = filtered.slice((page - 1) * PAGE, page * PAGE);

  const winLeft = (exp) => {
    if (!exp) return "";
    const ms = new Date(exp).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const days = Math.floor(ms / 86400000); const hours = Math.floor((ms % 86400000) / 3600000);
    return days >= 1 ? `${days}d ${hours}h left` : `${hours}h left`;
  };

  return (
    <Card className="mb-6 border-amber-200 bg-amber-50/40 p-6 shadow-sm" data-testid="admin-deletion-requests-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-amber-600" />
        <h3 className="text-lg font-semibold text-slate-800 font-heading">Data deletion approvals</h3>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="ml-auto h-8 w-52 text-xs" data-testid="admin-deletion-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="needs_action">Needs action</SelectItem>
            <SelectItem value="all">All requests</SelectItem>
            <SelectItem value="awaiting_activation">Awaiting activation</SelectItem>
            <SelectItem value="window_active">Window active</SelectItem>
            <SelectItem value="window_expired">Window expired</SelectItem>
            <SelectItem value="coach_requested">Coach forwarded</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="denied">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {shown.length === 0 ? <p className="py-2 text-sm text-slate-400" data-testid="admin-deletion-empty">No requests match this filter.</p> : (
        <div className="space-y-3">
          {shown.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-100 bg-white p-3" data-testid={`admin-deletion-request-${r.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{r.requester_name} <span className="font-normal text-slate-400">({DEL_ROLE_LABEL[r.requester_role] || r.requester_role})</span></p>
                <p className="text-xs text-slate-500">{r.requester_email} · requested {fmtDReq(r.created_at)}</p>
                {r.assignee_name && <p className="text-xs text-slate-500">Approved by <strong>{r.assignee_name}</strong> ({ROLE_LABEL[r.assignee_role] || r.assignee_role})</p>}
                {!r.assignee_name && r.assignee_role === "coach" && <p className="text-xs text-slate-500">Forwarded by a coach</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {r.status === "awaiting_activation" && (
                  <Button size="sm" disabled={busy === r.id} className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => act(r.id, "activate", "Delete window activated (7 days)")} data-testid={`admin-activate-window-${r.id}`}>
                    <Clock className="mr-1.5 h-3.5 w-3.5" />Activate delete window (7 days)
                  </Button>
                )}
                {r.status === "window_active" && (
                  <>
                    <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700" data-testid={`admin-window-active-${r.id}`}>Window active · {winLeft(r.window_expires_at)}</span>
                    <Button size="sm" variant="outline" disabled={busy === r.id} className="rounded-full" onClick={() => act(r.id, "extend", "Delete window extended by 7 days")} data-testid={`admin-extend-window-${r.id}`}>Extend 7 days</Button>
                    <Button size="sm" variant="outline" disabled={busy === r.id} className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => act(r.id, "deactivate", "Delete window deactivated — case marked completed")} data-testid={`admin-deactivate-window-${r.id}`}>Deactivate</Button>
                  </>
                )}
                {r.status === "window_expired" && (
                  <Button size="sm" variant="outline" disabled={busy === r.id} className="rounded-full" onClick={() => act(r.id, "activate", "Delete window re-activated (7 days)")} data-testid={`admin-reactivate-window-${r.id}`}>
                    <Clock className="mr-1.5 h-3.5 w-3.5" />Re-activate window
                  </Button>
                )}
                {r.status === "coach_requested" && (
                  <Button size="sm" disabled={busy === r.id} className="rounded-full bg-red-600 hover:bg-red-700" onClick={() => act(r.id, "execute", "Account permanently deleted")} data-testid={`admin-execute-deletion-${r.id}`}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete now
                  </Button>
                )}
                {["completed", "denied"].includes(r.status) && (
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.status === "denied" ? "bg-red-100 text-red-700" : r.deactivated ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`} data-testid={`admin-deletion-status-${r.id}`}>{r.status === "denied" ? "Declined" : r.deactivated ? "Closed — no deletion" : "Completed"}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-sm" data-testid="admin-deletion-pagination">
          <Button size="sm" variant="outline" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-deletion-prev">Prev</Button>
          <span className="text-slate-500">{page} / {pageCount}</span>
          <Button size="sm" variant="outline" className="rounded-full" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)} data-testid="admin-deletion-next">Next</Button>
        </div>
      )}
    </Card>
  );
}

function PendingStaffPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/pending-staff").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const act = async (uid, action, msg) => {
    setBusy(uid);
    try { await api.post(`/admin/pending-staff/${uid}/${action}`); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (items.length === 0) return null;
  return (
    <Card className="mb-6 border-sky-200 bg-sky-50/40 p-6 shadow-sm" data-testid="pending-staff-panel">
      <div className="mb-3 flex items-center gap-2">
        <UserX className="h-5 w-5 text-sky-600" />
        <h3 className="text-lg font-semibold text-slate-800 font-heading">Pending staff accounts</h3>
        <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-bold text-sky-700">{items.length}</span>
      </div>
      <div className="space-y-3">
        {items.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sky-100 bg-white p-3" data-testid={`pending-staff-${u.id}`}>
            <div>
              <p className="text-sm font-semibold text-slate-800">{u.first_name} {u.last_name} <span className="font-normal text-slate-400">({ROLE_LABEL[u.role] || u.role})</span></p>
              <p className="text-xs text-slate-500">{u.email}{u.team_name ? ` · ${u.team_name}` : ""}{u.campus_name ? ` · ${u.campus_name}` : ""}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy === u.id} className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => act(u.id, "approve", "Account approved")} data-testid={`pending-staff-approve-${u.id}`}>Approve</Button>
              <Button size="sm" variant="outline" disabled={busy === u.id} className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => act(u.id, "reject", "Account rejected")} data-testid={`pending-staff-reject-${u.id}`}>Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RoleRequestsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const load = useCallback(() => api.get("/role-requests").then(({ data }) => setItems((data.items || []).filter((r) => r.status === "pending"))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const act = async (rid, action, msg, body) => {
    setBusy(rid);
    try { await api.post(`/role-requests/${rid}/${action}`, body); toast.success(msg); setRejecting(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (items.length === 0) return null;
  return (
    <Card className="mb-6 border-violet-200 bg-violet-50/40 p-6 shadow-sm" data-testid="role-requests-panel">
      <div className="mb-3 flex items-center gap-2">
        <ArrowLeftRight className="h-5 w-5 text-violet-600" />
        <h3 className="text-lg font-semibold text-slate-800 font-heading">Role change requests</h3>
        <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-bold text-violet-700">{items.length}</span>
      </div>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-violet-100 bg-white p-3" data-testid={`role-request-${r.id}`}>
            <div>
              <p className="text-sm font-semibold text-slate-800"><span className="capitalize">{r.action}</span> {ROLE_LABEL[r.role] || r.role} <span className="font-normal text-slate-400">— {r.target_name || r.target_email || "existing account"}</span></p>
              <p className="text-xs text-slate-500">Requested by {r.requested_by_name} ({ROLE_LABEL[r.requested_by_role] || r.requested_by_role}) · {new Date(r.created_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" })}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy === r.id} className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => act(r.id, "approve", "Request approved")} data-testid={`role-request-approve-${r.id}`}>Approve</Button>
              <Button size="sm" variant="outline" disabled={busy === r.id} className={`rounded-full border-red-200 text-red-600 hover:bg-red-50 ${rejecting === r.id ? "bg-red-50" : ""}`} onClick={() => setRejecting(rejecting === r.id ? null : r.id)} data-testid={`role-request-reject-${r.id}`}>Reject</Button>
            </div>
            {rejecting === r.id && (
              <DeclineReasonBox busy={busy === r.id} onCancel={() => setRejecting(null)} testId={`role-request-decline-${r.id}`}
                onSend={(reason) => act(r.id, "reject", "Request rejected", { reason })} />
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function AdminAccounts() {
  const { user, impersonate } = useAuth();
  const { ref } = useReference();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [fDistrict, setFDistrict] = useState("all");
  const [fCampus, setFCampus] = useState("all");
  const [fTeam, setFTeam] = useState("all");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [pwAcct, setPwAcct] = useState(null);
  const [creating, setCreating] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeText, setPurgeText] = useState("");
  const [purging, setPurging] = useState(false);
  const [expungeAcct, setExpungeAcct] = useState(null);
  const [expungeText, setExpungeText] = useState("");
  const [expunging, setExpunging] = useState(false);
  const [reassign, setReassign] = useState(null);
  const [reassignForm, setReassignForm] = useState({ district_id: "", campus_id: "", team_id: "" });
  const [parentApprove, setParentApprove] = useState(null);
  const [parentApproveSel, setParentApproveSel] = useState([]);
  const [parentReject, setParentReject] = useState(null);
  const [parentLinks, setParentLinks] = useState(null);
  const [parentLinksSel, setParentLinksSel] = useState([]);
  const [parentBusy, setParentBusy] = useState(false);
  const [viewProfile, setViewProfile] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const PAGE_SIZE = 12;

  // Deep link from the dashboard request alert: /admin/accounts?panel=deletion|roles
  useEffect(() => {
    const tid = { deletion: "admin-deletion-requests-panel", roles: "role-requests-panel" }[searchParams.get("panel")];
    if (!tid) return undefined;
    const ring = ["ring-2", "ring-sky-400", "ring-offset-2"];
    let tries = 0;
    const t = setInterval(() => {
      const el = document.querySelector(`[data-testid="${tid}"]`);
      if (!el && ++tries < 20) return;
      clearInterval(t);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add(...ring);
      setTimeout(() => el.classList.remove(...ring), 2500);
    }, 150);
    return () => clearInterval(t);
  }, [searchParams]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/accounts");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const r = searchParams.get("role");
    const st = searchParams.get("status");
    if (r) setRole(r);
    if (st) setStatus(st);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deactivatedCount = items.filter((a) => a.is_active === false).length;
  const doPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/accounts/purge-deactivated");
      toast.success(`Purged ${data.deleted_accounts} account(s) and ${data.deleted_records} record(s)`);
      setPurgeOpen(false); setPurgeText(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPurging(false); }
  };

  const doImpersonate = async (a) => {
    try {
      const u = await impersonate(a.id);
      toast.success(`Now viewing as ${a.first_name} ${a.last_name}`, { duration: 2000 });
      navigate(u.role === "coach" ? "/coach/dashboard" : u.role === "administrator" ? "/admin/dashboard" : "/member/dashboard");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doExpunge = async () => {
    setExpunging(true);
    try {
      const { data } = await api.post(`/admin/accounts/${expungeAcct.id}/expunge`);
      toast.success(`Deleted account and ${data.deleted_records} record(s)`);
      setExpungeAcct(null); setExpungeText(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setExpunging(false); }
  };

  const doReject = async (a) => {
    try {
      await api.post(`/admin/team-members/${a.id}/reject`);
      toast.success("Membership request rejected"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openReassign = (a) => { setReassign(a); setReassignForm({ district_id: a.district_id || "", campus_id: a.campus_id || "", team_id: a.team_id || "" }); };
  const doReassign = async () => {
    if (!reassignForm.team_id) { toast.error("Select district, campus and team"); return; }
    try {
      await api.post(`/admin/team-members/${reassign.id}/reassign`, reassignForm);
      toast.success("Reassigned"); setReassign(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reCampuses = ref.campuses.filter((c) => c.district_id === reassignForm.district_id);
  const reTeams = ref.teams.filter((t) => t.campus_id === reassignForm.campus_id);

  const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const fetchParentDetail = async (id) => {
    const { data } = await api.get("/admin/parents");
    return (data.items || []).find((p) => p.id === id);
  };
  const openParentApprove = async (a) => {
    const p = await fetchParentDetail(a.id);
    if (!p) { toast.error("Could not load parent details"); return; }
    const rosterIds = new Set((p.team_roster || []).map((m) => m.id));
    setParentApprove(p);
    setParentApproveSel((p.suggested_students || []).map((s) => s.id).filter((id) => rosterIds.has(id)));
  };
  const doParentApprove = async () => {
    if (parentApproveSel.length === 0) { toast.error("Select at least one student to link"); return; }
    setParentBusy(true);
    try {
      await api.post(`/admin/parents/${parentApprove.id}/approve`, { student_ids: parentApproveSel });
      toast.success("Parent approved & linked"); setParentApprove(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setParentBusy(false); }
  };
  const doParentReject = async () => {
    try {
      await api.post(`/admin/parents/${parentReject.id}/reject`);
      toast.success("Parent request rejected"); setParentReject(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openParentLinks = async (a) => {
    const p = await fetchParentDetail(a.id);
    if (!p) { toast.error("Could not load parent details"); return; }
    setParentLinks(p);
    setParentLinksSel((p.linked_students || []).map((s) => s.id));
  };
  const doParentLinks = async () => {
    setParentBusy(true);
    try {
      await api.put(`/admin/parents/${parentLinks.id}`, {
        first_name: parentLinks.first_name, last_name: parentLinks.last_name, email: parentLinks.email,
        is_active: parentLinks.is_active !== false, linked_student_ids: parentLinksSel,
      });
      toast.success("Linked students updated"); setParentLinks(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setParentBusy(false); }
  };

  const openViewProfile = async (a) => {
    setViewLoading(true);
    try {
      const { data } = await api.get(`/admin/accounts/${a.id}/profile`);
      setViewProfile(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setViewLoading(false); }
  };

  const doUnlockPhonebook = async (a) => {
    try {
      await api.post(`/admin/accounts/${a.id}/phonebook-unlock`);
      toast.success(`Phonebook unlocked for ${a.first_name}. Their PIN was reset.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const teamCodeById = ref.teams.reduce((m, t) => { m[t.id] = t.code; return m; }, {});
  const filtered = items.filter((a) => {
    if (role !== "all" && a.role !== role) return false;
    if (status !== "all" && (a.assignment_status || "") !== status) return false;
    if (fDistrict !== "all" && a.district_id !== fDistrict) return false;
    if (fCampus !== "all" && a.campus_id !== fCampus) return false;
    if (fTeam !== "all" && teamCodeById[a.team_id] !== fTeam) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [a.first_name, a.last_name, a.email, a.district_name, a.campus_name, a.team_name].some((v) => (v || "").toLowerCase().includes(s));
  }).sort((a, b) => {
    const an = `${a.last_name || ""} ${a.first_name || ""}`.trim();
    const bn = `${b.last_name || ""} ${b.first_name || ""}`.trim();
    return an.localeCompare(bn, undefined, { sensitivity: "base" });
  });
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterCampusOpts = ref.campuses.filter((c) => fDistrict === "all" || c.district_id === fDistrict);
  const filterTeamScope = ref.teams.filter((t) => fCampus === "all" ? (fDistrict === "all" || t.district_id === fDistrict) : t.campus_id === fCampus);
  const filterTeamOpts = Object.values(filterTeamScope.reduce((m, t) => { if (t.code && !m[t.code]) m[t.code] = t; return m; }, {}))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div>
      <PageHeader title="Users" subtitle="View and manage every account in RallyPointGo"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => { setPurgeText(""); setPurgeOpen(true); }} disabled={deactivatedCount === 0} className="rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid="purge-deactivated-btn"><Trash2 className="mr-2 h-4 w-4" /> Purge deactivated ({deactivatedCount})</Button>
            <Button onClick={() => setCreating(true)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="add-account-btn"><Plus className="mr-2 h-4 w-4" /> Add account</Button>
          </div>
        } />

      <PendingStaffPanel />
      <RoleRequestsPanel />
      <DeletionRequestsPanel />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, email, district…" className="pl-9" data-testid="accounts-search" />
        </div>
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="accounts-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="team_member">Team Members</SelectItem>
            <SelectItem value="coach">Coaches</SelectItem>
            <SelectItem value="parent">Parents</SelectItem>
            <SelectItem value="campus_administrator">Campus Admins</SelectItem>
            <SelectItem value="district_administrator">District Admins</SelectItem>
            <SelectItem value="administrator">System Administrators</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="accounts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fDistrict} onValueChange={(v) => { setFDistrict(v); setFCampus("all"); setFTeam("all"); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="accounts-district-filter"><SelectValue placeholder="District" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCampus} onValueChange={(v) => { setFCampus(v); setFTeam("all"); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="accounts-campus-filter"><SelectValue placeholder="Campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {filterCampusOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fTeam} onValueChange={(v) => { setFTeam(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="accounts-team-filter"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teams</SelectItem>
            {filterTeamOpts.map((t) => <SelectItem key={t.code} value={t.code}>{t.name} ({t.code})</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500" data-testid="accounts-count">{filtered.length} accounts</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No accounts found" description="Try adjusting your search or filter." testId="accounts-empty" />
      ) : (
        <>
          <Card className="border-slate-200 shadow-sm overflow-x-auto">
            <Table data-testid="accounts-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Name", "Email", "Role", "District / Team", "Campus", "Status", "Actions"].map((h) => (
                    <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((a) => (
                  <TableRow key={a.id} data-testid={`account-row-${a.id}`} className={a.is_active === false ? "opacity-50" : ""}>
                    <TableCell className="font-semibold text-slate-800">{a.last_name}, {a.first_name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{a.email}</TableCell>
                    <TableCell><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{ROLE_LABEL[a.role] || a.role}</span></TableCell>
                    <TableCell className="text-sm text-slate-500">{a.is_staff ? "Staff" : a.role === "administrator" ? "—" : `${a.district_name || "—"}${a.team_name ? ` · ${a.team_name}` : ""}`}</TableCell>
                    <TableCell className="text-sm text-slate-500" data-testid={`account-campus-${a.id}`}>{a.campus_name || "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={a.is_active === false ? "inactive" : "active"} />
                        {(a.role === "team_member" || a.role === "parent") && a.assignment_status && a.assignment_status !== "approved" && (
                          <StatusBadge status={a.assignment_status} />
                        )}
                        {a.role === "coach" && a.phonebook_locked && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700" data-testid={`phonebook-locked-badge-${a.id}`}>Phonebook locked</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(a.role === "team_member" || a.role === "parent") && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-600" title="View full profile" disabled={viewLoading} onClick={() => openViewProfile(a)} data-testid={`account-view-profile-${a.id}`}><Eye className="h-4 w-4" /></Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => setEdit(a)} data-testid={`account-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Set password" onClick={() => setPwAcct(a)} data-testid={`account-password-${a.id}`}><KeyRound className="h-4 w-4" /></Button>
                        {a.role === "coach" && a.phonebook_locked && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" title="Unlock phonebook & reset PIN" onClick={() => doUnlockPhonebook(a)} data-testid={`account-unlock-phonebook-${a.id}`}><LockOpen className="h-4 w-4" /></Button>
                        )}
                        {a.role === "team_member" && (
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Reassign team" onClick={() => openReassign(a)} data-testid={`account-reassign-${a.id}`}><ArrowLeftRight className="h-4 w-4" /></Button>
                        )}
                        {a.role === "team_member" && a.assignment_status !== "rejected" && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Reject membership request" onClick={() => doReject(a)} data-testid={`account-reject-${a.id}`}><UserX className="h-4 w-4" /></Button>
                        )}
                        {a.role === "parent" && a.assignment_status === "pending" && (
                          <>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" title="Approve & link parent" onClick={() => openParentApprove(a)} data-testid={`account-parent-approve-${a.id}`}><Check className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Reject parent request" onClick={() => setParentReject(a)} data-testid={`account-parent-reject-${a.id}`}><X className="h-4 w-4" /></Button>
                          </>
                        )}
                        {a.role === "parent" && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-600" title="Manage linked students" onClick={() => openParentLinks(a)} data-testid={`account-parent-links-${a.id}`}><Link2 className="h-4 w-4" /></Button>
                        )}
                        {a.id !== user?.id && a.is_active !== false && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-600" title="Login as" onClick={() => doImpersonate(a)} data-testid={`account-impersonate-${a.id}`}><LogIn className="h-4 w-4" /></Button>
                        )}
                        {a.id !== user?.id && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Delete account permanently" onClick={() => { setExpungeText(""); setExpungeAcct(a); }} data-testid={`account-expunge-${a.id}`}><Skull className="h-4 w-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPage={setPage} />
        </>
      )}

      {edit && <EditDialog open={!!edit} setOpen={(o) => !o && setEdit(null)} acct={edit} onDone={load} />}
      {pwAcct && <PasswordDialog open={!!pwAcct} setOpen={(o) => !o && setPwAcct(null)} acct={pwAcct} />}
      <CreateDialog open={creating} setOpen={setCreating} onDone={load} />

      <Dialog open={!!viewProfile} onOpenChange={(o) => !o && setViewProfile(null)}>
        <DialogContent data-testid="account-view-profile-dialog" className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Full profile</DialogTitle>
            <DialogDescription>Unmasked contact details — this view is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          {viewProfile && (
            <div className="divide-y divide-slate-100 text-sm">
              <ViewRow label="Name" value={`${viewProfile.first_name || ""} ${viewProfile.last_name || ""}`.trim()} />
              <ViewRow label="Email" value={viewProfile.email} />
              <ViewRow label="Role" value={ROLE_LABEL[viewProfile.role] || viewProfile.role} />
              <ViewRow label="District" value={viewProfile.district_name} />
              <ViewRow label="Team" value={viewProfile.team_name ? `${viewProfile.team_name}${viewProfile.team_code ? ` (${viewProfile.team_code})` : ""}` : null} />
              <ViewRow label="Assignment status" value={viewProfile.assignment_status} />
              {viewProfile.role === "team_member" && <>
                <ViewRow label="Graduation year" value={viewProfile.graduation_year} />
                <ViewRow label="Member phone" value={viewProfile.member_phone} />
                <ViewRow label="Parent/Guardian name" value={[viewProfile.guardian_first_name, viewProfile.guardian_last_name].filter(Boolean).join(" ")} />
                <ViewRow label="Parent/Guardian phone" value={viewProfile.guardian_phone} />
                <ViewRow label="Parent/Guardian email" value={viewProfile.guardian_email} />
              </>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassign} onOpenChange={(o) => !o && setReassign(null)}>
        <DialogContent data-testid="account-reassign-dialog" className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Reassign member</DialogTitle>
            <DialogDescription>{reassign ? `Move ${reassign.first_name} ${reassign.last_name} to a different district, campus and team.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>District</Label>
              <Select value={reassignForm.district_id} onValueChange={(v) => setReassignForm((f) => ({ ...f, district_id: v, campus_id: "", team_id: "" }))}>
                <SelectTrigger data-testid="account-reassign-district-select"><SelectValue placeholder="District" /></SelectTrigger>
                <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Campus</Label>
              <Select value={reassignForm.campus_id} onValueChange={(v) => setReassignForm((f) => ({ ...f, campus_id: v, team_id: "" }))}>
                <SelectTrigger data-testid="account-reassign-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                <SelectContent>{reCampuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Team</Label>
              <Select value={reassignForm.team_id} onValueChange={(v) => setReassignForm((f) => ({ ...f, team_id: v }))}>
                <SelectTrigger data-testid="account-reassign-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
                <SelectContent>{reTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={doReassign} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="account-reassign-save-btn">Reassign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!parentApprove} onOpenChange={(o) => !o && setParentApprove(null)}>
        <DialogContent data-testid="account-parent-approve-dialog" className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Approve parent</DialogTitle>
            <DialogDescription>Approve this parent/guardian request and link them to their student(s).</DialogDescription>
          </DialogHeader>
          {parentApprove && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{parentApprove.first_name} {parentApprove.last_name} · {parentApprove.email}</p>
              {!parentApprove.email_matched && <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700">This email didn't match any student's emergency contact. Manually select the student(s) to link.</p>}
              <div className="space-y-2"><Label className="text-xs">Link to student(s)</Label>
                <StudentPicker roster={parentApprove.team_roster || []} selected={parentApproveSel} onToggle={(id) => setParentApproveSel((s) => toggle(s, id))} testPrefix="account-approve" />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={doParentApprove} disabled={parentBusy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="account-parent-approve-confirm"><Check className="mr-1 h-4 w-4" /> Approve &amp; link</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!parentLinks} onOpenChange={(o) => !o && setParentLinks(null)}>
        <DialogContent data-testid="account-parent-links-dialog" className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Manage linked students</DialogTitle>
            <DialogDescription>{parentLinks ? `Choose which student(s) ${parentLinks.first_name} ${parentLinks.last_name} can view.` : ""}</DialogDescription>
          </DialogHeader>
          {parentLinks && (
            <div className="space-y-2">
              <Label className="text-xs">Linked students</Label>
              <StudentPicker roster={parentLinks.team_roster || []} selected={parentLinksSel} onToggle={(id) => setParentLinksSel((s) => toggle(s, id))} testPrefix="account-links" />
            </div>
          )}
          <DialogFooter><Button onClick={doParentLinks} disabled={parentBusy} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="account-parent-links-save">Save links</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!parentReject} onOpenChange={(o) => !o && setParentReject(null)} title="Reject parent request?"
        description={parentReject ? `${parentReject.first_name} ${parentReject.last_name} will not be able to log in.` : ""}
        confirmText="Reject" destructive onConfirm={doParentReject} testId="account-parent-reject-dialog" />

      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!o) { setPurgeOpen(false); setPurgeText(""); } }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="purge-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Purge deactivated accounts</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This <strong>permanently deletes {deactivatedCount} deactivated account(s)</strong> — members, coaches and administrators — along with their merit cases, merit requests and lettering submissions. <strong>This cannot be undone.</strong>
            </p>
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              Make sure you've already run the all-district <strong>Merits</strong> and <strong>Lettering</strong> detail exports — those downloaded files are your only archive afterward. Your own account is never deleted.
            </p>
            <div className="space-y-2">
              <Label>Type <span className="font-mono font-semibold">PURGE</span> to confirm</Label>
              <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="PURGE" data-testid="purge-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setPurgeOpen(false); setPurgeText(""); }} data-testid="purge-cancel-btn">Cancel</Button>
            <Button disabled={purgeText !== "PURGE" || purging} onClick={doPurge} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="purge-confirm-btn">{purging ? "Purging…" : "Purge permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!expungeAcct} onOpenChange={(o) => { if (!o) { setExpungeAcct(null); setExpungeText(""); } }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="expunge-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-600 flex items-center gap-2"><Skull className="h-5 w-5" /> Permanently delete account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This <strong>permanently deletes {expungeAcct ? `${expungeAcct.first_name} ${expungeAcct.last_name} (${expungeAcct.email})` : "this account"}</strong> along with every record tied to it — merit cases, merit requests, lettering submissions, choice responses and notifications. <strong>⚠️ This cannot be undone.</strong>
            </p>
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              If you may need this data later, run your <strong>Merits</strong> and <strong>Lettering</strong> exports first — the downloaded files are your only archive afterward.
            </p>
            <div className="space-y-2">
              <Label>Type <span className="font-mono font-semibold">EXPUNGE</span> to confirm</Label>
              <Input value={expungeText} onChange={(e) => setExpungeText(e.target.value)} placeholder="EXPUNGE" data-testid="expunge-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setExpungeAcct(null); setExpungeText(""); }} data-testid="expunge-cancel-btn">Cancel</Button>
            <Button disabled={expungeText !== "EXPUNGE" || expunging} onClick={doExpunge} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="expunge-confirm-btn">{expunging ? "Deleting…" : "Delete permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


